"use client"

import { useState, useEffect, useCallback, memo } from "react"
import { useLocation } from "react-router-dom"
import { api } from "../utils/auth"
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js"

// Define styles outside the component to prevent re-creation on every render
const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    padding: "2rem 1rem",
  },
  maxWidth: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  header: {
    textAlign: "center",
    marginBottom: "2rem",
  },
  title: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: "#1a202c",
    marginBottom: "0.5rem",
  },
  subtitle: {
    color: "#666",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "2rem",
    marginBottom: "2rem",
  },
  card: {
    backgroundColor: "white",
    padding: "1.5rem",
    borderRadius: "0.5rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
  },
  cardTitle: {
    fontSize: "1.25rem",
    fontWeight: "600",
  },
  badge: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
    padding: "0.25rem 0.75rem",
    borderRadius: "1rem",
    fontSize: "0.875rem",
  },
  flexBetween: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.5rem",
  },
  separator: {
    margin: "1rem 0",
    border: "none",
    borderTop: "1px solid #e5e7eb",
  },
  totalAmount: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  amount: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    color: "#16a34a",
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "1.5rem",
  },
  qrCode: {
    width: "192px",
    height: "192px",
    backgroundColor: "white",
    border: "2px solid #e5e7eb",
    borderRadius: "0.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.875rem",
    color: "#666",
    textAlign: "center",
  },
  qrImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: "0.375rem",
  },
  inputGroup: {
    marginBottom: "1.5rem",
    width: "100%",
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: "500",
    marginBottom: "0.5rem",
  },
  inputFlex: {
    display: "flex",
    gap: "0.5rem",
  },
  input: {
    flex: 1,
    padding: "0.5rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.375rem",
    fontFamily: "monospace",
    backgroundColor: "#f9fafb",
  },
  copyButton: {
    padding: "0.5rem 1rem",
    backgroundColor: "#f3f4f6",
    border: "1px solid #d1d5db",
    borderRadius: "0.375rem",
    cursor: "pointer",
  },
  instructions: {
    backgroundColor: "#dbeafe",
    padding: "1rem",
    borderRadius: "0.5rem",
  },
  instructionsTitle: {
    fontWeight: "500",
    color: "#1e40af",
    marginBottom: "0.5rem",
  },
  instructionsList: {
    fontSize: "0.875rem",
    color: "#1e40af",
    paddingLeft: "1rem",
  },
  transactionInput: {
    width: "100%",
    padding: "0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.375rem",
    fontFamily: "monospace",
    fontSize: "1rem",
  },
  submitButton: {
    width: "100%",
    padding: "0.75rem",
    backgroundColor: "#007BFF",
    color: "white",
    border: "none",
    borderRadius: "0.375rem",
    fontSize: "1.125rem",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "1rem",
    marginTop: "1rem"
  },
  submitButtonDisabled: {
    backgroundColor: "#9ca3af",
    cursor: "not-allowed",
  },
  footer: {
    textAlign: "center",
    fontSize: "0.875rem",
    color: "#666",
    marginTop: "2rem"
  },
  successContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  successCard: {
    backgroundColor: "white",
    padding: "2rem",
    borderRadius: "0.5rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    maxWidth: "400px",
    width: "100%",
    textAlign: "center",
  },
  successIcon: {
    width: "48px",
    height: "48px",
    backgroundColor: "#dcfce7",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1rem",
    fontSize: "1.5rem",
  },
  successTitle: {
    color: "#16a34a",
    marginBottom: "0.5rem",
  },
  transactionBox: {
    backgroundColor: "#f9fafb",
    padding: "1rem",
    borderRadius: "0.5rem",
    marginBottom: "1rem",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "2rem",
    color: "#6b7280",
  },
  errorBanner: {
    backgroundColor: "#fee2e2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
    padding: "1rem",
    borderRadius: "0.375rem",
    marginBottom: "1rem",
    textAlign: "center",
  },
  refreshButton: {
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: "0.375rem",
    cursor: "pointer",
    fontSize: "0.875rem",
    marginTop: "0.5rem",
  },
  paymentOptionCard: {
    backgroundColor: "white",
    padding: "1.5rem",
    borderRadius: "0.5rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "border-color 0.2s ease-in-out",
    marginBottom: "1rem",
    textAlign: "center",
  },
  paymentOptionTitle: {
    fontSize: "1.125rem",
    fontWeight: "600",
    marginBottom: "0.25rem",
  },
  paymentOptionDescription: {
    fontSize: "0.875rem",
    color: "#666",
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '0.5rem',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
    zIndex: 1001,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  closeButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666',
  }
};

// Memoized UpiModal component
const UpiModal = memo(({
  setShowModal,
  paymentSettings,
  amount,
  transactionId,
  setTransactionId,
  isSubmitting,
  paypalPaid,
  showToast,
  handleSubmitTransaction,
  handleCopyUPI
}) => (
  <div style={styles.modalOverlay}>
    <div style={styles.modalContent}>
      <button onClick={() => setShowModal(null)} style={styles.closeButton}>×</button>
      <h3 style={{ ...styles.cardTitle, marginBottom: "0.5rem" }}>UPI Payment Details</h3>
      <p style={{ color: "#666", marginBottom: "1.5rem", fontSize: "0.875rem" }}>
        Scan with any UPI app to make payment, or use the UPI ID
      </p>

      {/* QR Code */}
      <div style={styles.qrContainer}>
        <div style={styles.qrCode}>
          {paymentSettings.qrCodeUrl ? (
            <img
              src={paymentSettings.qrCodeUrl || "/placeholder.svg"}
              alt="Payment QR Code"
              style={styles.qrImage}
              onError={(e) => {
                e.target.style.display = "none"
                e.target.nextSibling.style.display = "block"
              }}
            />
          ) : null}
          <div style={{ display: paymentSettings.qrCodeUrl ? "none" : "block" }}>
            QR Code for ${amount.toFixed(2)}
            <br />
            (No QR Code Available)
          </div>
        </div>
      </div>

      {/* UPI ID */}
      <div style={styles.inputGroup}>
        <label style={styles.label}>UPI ID</label>
        <div style={styles.inputFlex}>
          <input type="text" value={paymentSettings.upiId} readOnly style={styles.input} />
          <button onClick={handleCopyUPI} style={styles.copyButton}>
            📋
          </button>
        </div>
      </div>

      {/* Payment Instructions */}
      <div style={styles.instructions}>
        <h4 style={styles.instructionsTitle}>UPI Payment Instructions:</h4>
        <ol style={styles.instructionsList}>
          <li>Scan the QR code with any UPI app (e.g., GPay, PhonePe, Paytm)</li>
          <li>Or manually enter the UPI ID above</li>
          <li>Pay the exact amount: ${amount.toFixed(2)}</li>
          <li>Copy the transaction ID (UTR) after payment</li>
          <li>Enter the transaction ID below and submit</li>
        </ol>
      </div>

      {/* Transaction ID Submission for UPI */}
      <div style={{ ...styles.inputGroup, marginTop: "1.5rem" }}>
        <label style={styles.label}>UPI Transaction ID (UTR) *</label>
        <input
          type="text"
          placeholder="Enter your UPI transaction ID (UTR)"
          value={transactionId}
          onChange={(e) => setTransactionId(e.target.value)}
          style={styles.transactionInput}
          disabled={isSubmitting || paypalPaid} // Disable if PayPal is paid or submitting
        />
        <p style={{ fontSize: "0.875rem", color: "#666", marginTop: "0.25rem" }}>
          You can find this in your UPI app after successful payment
        </p>
      </div>

      <button
        onClick={() => handleSubmitTransaction("UPI")}
        disabled={isSubmitting || !transactionId.trim() || !paymentSettings.isActive || paypalPaid}
        style={{
          ...styles.submitButton,
          ...(isSubmitting || !transactionId.trim() || !paymentSettings.isActive || paypalPaid
            ? styles.submitButtonDisabled
            : {}),
        }}
      >
        {isSubmitting ? "Submitting UPI Transaction..." : "Submit UPI Transaction"}
      </button>
    </div>
  </div>
));

// Memoized PaypalModal component
const PaypalModal = memo(({
  setShowModal,
  paypalConfigured,
  paypalPaid,
  initialOptions,
  createOrder,
  onApprove, // This onApprove will now handle the backend API call
  onError,
  onCancel,
  transactionId,
  setTransactionId,
  isSubmitting,
  paymentSettings,
  // handleSubmitTransaction is no longer directly called from onApprove in this version
  // but kept for other parts of PaymentsPage if needed.
  handleSubmitTransaction
}) => (
  <div style={styles.modalOverlay}>
    <div style={styles.modalContent}>
      <button onClick={() => setShowModal(null)} style={styles.closeButton}>×</button>
      <h3 style={{ ...styles.cardTitle, marginBottom: "1rem" }}>PayPal Payment</h3>
      {paypalConfigured ? (
        !paypalPaid ? (
          <PayPalScriptProvider options={initialOptions}>
            <PayPalButtons
              style={{ layout: "vertical", color: "blue", shape: "rect", label: "paypal" }}
              createOrder={createOrder}
              onApprove={onApprove}
              onError={onError}
              onCancel={onCancel}
            />
          </PayPalScriptProvider>
        ) : (
          <p style={{ textAlign: "center", color: "#16a34a", fontWeight: "bold" }}>
            PayPal payment successful! Your plan will be updated shortly.
          </p>
        )
      ) : (
        <div style={styles.errorBanner}>
          PayPal payment is currently unavailable. Please contact the administrator to configure PayPal Client ID.
        </div>
      )}

      {/* Transaction ID Submission for PayPal: This part is now less critical for immediate plan update
          as the onApprove directly calls backend, but can remain for user's record keeping if desired. */}
      {paypalPaid && ( // Only show if PayPal payment was successful
        <div style={{ ...styles.inputGroup, marginTop: "1.5rem" }}>
          <label style={styles.label}>PayPal Transaction ID *</label>
          <input
            type="text"
            placeholder="PayPal Transaction ID"
            value={transactionId}
            readOnly // Make it read-only as it's populated by PayPal details
            style={styles.transactionInput}
          />
          <p style={{ fontSize: "0.875rem", color: "#666", marginTop: "0.25rem" }}>
            This ID was automatically captured from PayPal after successful payment.
          </p>
        </div>
      )}

      {/* The "Submit PayPal Transaction" button is now potentially redundant for plan update
          if onApprove handles everything, but can be kept if there's a separate "submission"
          flow desired after initial PayPal capture. For now, onApprove handles the update. */}
      {/* <button
        onClick={() => handleSubmitTransaction("PayPal")}
        disabled={isSubmitting || !transactionId.trim() || !paymentSettings.isActive}
        style={{
          ...styles.submitButton,
          ...(isSubmitting || !transactionId.trim() || !paymentSettings.isActive
            ? styles.submitButtonDisabled
            : {}),
        }}
      >
        {isSubmitting ? "Submitting PayPal Transaction..." : "Submit PayPal Transaction"}
      </button> */}
    </div>
  </div>
));

const PaymentPage = () => {
  const location = useLocation()
  const [transactionId, setTransactionId] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paymentSubmitted, setPaymentSubmitted] = useState(false) // This now primarily reflects if backend update was triggered
  const [showModal, setShowModal] = useState(null) // 'upi' or 'paypal'
  const [paymentSettings, setPaymentSettings] = useState({
    upiId: "merchant@paytm",
    paypalClientId: "", // Now fetched from backend
    qrCodeUrl: "",
    isActive: true,
  })
  const [loadingSettings, setLoadingSettings] = useState(true)
  const [settingsError, setSettingsError] = useState(false)
  const [paypalPaid, setPaypalPaid] = useState(false) // Indicates if PayPal flow itself was successful

  // Get plan details from URL params
  const urlParams = new URLSearchParams(location.search)
  const plan = urlParams.get("plan") || "professional"
  const billingCycle = urlParams.get("billing") || "monthly"
  const urlAmount = urlParams.get("amount")

  // Plan pricing (used as fallback if urlAmount is missing or for display consistency)
  const planPricing = {
    free: { monthly: 0, yearly: 0 },
    professional: { monthly: 29.99, yearly: 299.99 },
    enterprise: { monthly: 99.99, yearly: 999.99 },
  }

  // Determine the amount to display and process
  const amount = urlAmount ? Number.parseFloat(urlAmount) : planPricing[plan]?.[billingCycle] || 29.99
  const planName = plan.charAt(0).toUpperCase() + plan.slice(1)

  // Effect to manage body scroll
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden'; // Disable scrolling on the body
    } else {
      document.body.style.overflow = 'unset'; // Enable scrolling on the body
    }

    // Cleanup function to ensure scroll is re-enabled if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]); // Rerun when showModal changes

  // Fetch payment settings from backend API (now includes PayPal Client ID)
  useEffect(() => {
    const fetchPaymentSettings = async () => {
      setLoadingSettings(true)
      setSettingsError(false)

      try {
        const response = await api.get("/payment-settings")
        if (response.status === 200) {
          setPaymentSettings(response.data)
          console.log("Payment settings loaded:", response.data)
        } else {
          throw new Error("Failed to fetch payment settings")
        }
      } catch (error) {
        console.error("Error fetching payment settings:", error)
        setSettingsError(true)
        // Use fallback settings if API fails
        setPaymentSettings({
          upiId: "merchant@paytm",
          paypalClientId: "",
          qrCodeUrl: "",
          isActive: true,
        })
      } finally {
        setLoadingSettings(false)
      }
    }

    fetchPaymentSettings()
  }, []) // Removed setInterval and its cleanup

  const showToast = useCallback((message, type = "success") => {
    alert(message) // Using alert for simplicity, replace with a proper toast library
  }, []);

  const handleCopyUPI = useCallback(() => {
    navigator.clipboard.writeText(paymentSettings.upiId)
    showToast("UPI ID copied to clipboard!")
  }, [paymentSettings.upiId, showToast]);

  // handleSubmitTransaction is primarily for UPI now, or if you need a separate "submission" after PayPal capture
  const handleSubmitTransaction = useCallback(async (method) => {
    console.log("🔄 Starting transaction submission for", method, "...")
    console.log("Transaction ID:", transactionId.trim())
    console.log("Amount:", amount)
    console.log("Plan:", plan)
    console.log("Billing Cycle:", billingCycle)

    if (!transactionId.trim()) {
      console.log("❌ No transaction ID provided")
      showToast("Please enter a valid transaction ID", "error")
      return
    }

    if (!paymentSettings.isActive) {
      console.log("❌ Payment system inactive")
      showToast("Payment system is currently unavailable. Please try again later.", "error")
      return
    }

    setIsSubmitting(true)
    console.log("🚀 Making API call to /transactions...")

    try {
      const response = await api.post("/transactions", {
        transactionId: transactionId.trim(),
        amount: Number.parseFloat(amount),
        plan,
        billingCycle,
        paymentMethod: method, // Use the passed method
      })

      console.log("✅ API Response:", response)
      if (response.status === 200 || response.status === 201) {
        setPaymentSubmitted(true)
        showToast("Transaction submitted successfully! Your plan will be verified and updated shortly.");
        setShowModal(null); // Close modal on successful submission
      } else {
        throw new Error("Failed to submit transaction")
      }
    } catch (error) {
      console.error("❌ Error submitting transaction:", error)
      if (error.response?.status === 409) {
        showToast("This transaction ID has already been submitted. Please use a different transaction ID.", "error")
      } else if (error.response?.data?.error) {
        showToast(error.response.data.error, "error")
      } else {
        showToast("Failed to submit transaction. Please try again.", "error")
      }
    } finally {
      console.log("🏁 Transaction submission completed")
      setIsSubmitting(false)
    }
  }, [transactionId, amount, plan, billingCycle, paymentSettings.isActive, showToast, setShowModal]);

  // PayPal Integration - now uses PayPal Client ID from backend
  const PAYPAL_CLIENT_ID = paymentSettings.paypalClientId || process.env.REACT_APP_PAYPAL_CLIENT_ID || ""

  // Show error if PayPal Client ID is not configured
  const paypalConfigured = PAYPAL_CLIENT_ID && PAYPAL_CLIENT_ID.trim() !== ""

  const initialOptions = {
    clientId: PAYPAL_CLIENT_ID,
    currency: "USD",
    intent: "capture",
  }

  const createOrder = useCallback((data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            value: amount.toFixed(2),
            currency_code: "USD",
          },
          description: `${planName} Plan - ${billingCycle} billing`,
        },
      ],
    })
  }, [amount, planName, billingCycle]);

  const onApprove = useCallback((data, actions) => {
    return actions.order
      .capture()
      .then(async (details) => {
        console.log("PayPal payment successful:", details);
        const paypalOrderID = details.id;

        // You need to get the actual userId from your authentication context.
        // For demonstration, let's assume you have a way to get it, e.g., from a context API or local storage.
        // Replace 'YOUR_USER_ID_HERE' with the actual user ID.
        const userId = localStorage.getItem('userId'); // Example: if you store it in localStorage
        // const { user } = useAuth(); // Example: if you have an AuthContext
        // const userId = user?.id;

        if (!userId) {
          console.error("User ID not found. Cannot update plan.");
          showToast("Payment successful, but failed to update your plan. Please log in again or contact support.", "error");
          setPaypalPaid(true); // Still mark PayPal as paid
          setTransactionId(paypalOrderID); // Set transaction ID for user reference
          return;
        }

        try {
          setIsSubmitting(true); // Indicate that backend submission is in progress
          const response = await api.post("/transactions/verify-paypal", {
            orderID: paypalOrderID,
            amount: parseFloat(details.purchase_units[0].amount.value), // Ensure amount is number
            plan: plan, // The plan chosen by the user
            userId: userId, // Pass the authenticated user's ID
          });

          if (response.data.success) {
            console.log("Backend verification and plan update successful:", response.data);
            showToast("Payment verified and plan updated successfully!");
            setPaymentSubmitted(true); // Mark as submitted to show success message
            setPaypalPaid(true); // Mark PayPal as paid
            setTransactionId(paypalOrderID); // Set transaction ID for display
            setShowModal(null); // Close the PayPal modal
          } else {
            console.error("Backend verification failed:", response.data.message);
            showToast(response.data.message || "Payment verification failed.", "error");
            setPaypalPaid(true); // Mark PayPal as paid
            setTransactionId(paypalOrderID); // Set transaction ID for user reference
          }
        } catch (error) {
          console.error("Error calling backend verify-paypal API:", error);
          showToast("An error occurred during payment verification and plan update. Please contact support.", "error");
          setPaypalPaid(true); // Mark PayPal as paid
          setTransactionId(paypalOrderID); // Set transaction ID for user reference
        } finally {
          setIsSubmitting(false); // End submission state
        }
      })
      .catch((error) => {
        console.error("Error capturing PayPal payment:", error);
        showToast("There was an error processing your PayPal payment. Please try again.", "error");
      });
  }, [showToast, setTransactionId, setPaypalPaid, setIsSubmitting, amount, plan, setShowModal]); // Dependencies updated

  const onError = useCallback((err) => {
    console.error("PayPal Error:", err)
    showToast("PayPal payment failed. Please try again.", "error")
  }, [showToast]);

  const onCancel = useCallback((data) => {
    console.log("PayPal payment cancelled:", data)
    showToast("PayPal payment cancelled.", "info")
  }, [showToast]);

  // Handle Free plan selected (direct navigation to dashboard)
  if (plan === "free" || amount === 0) {
    return (
      <div style={styles.successContainer}>
        <div style={styles.successCard}>
          <h2 style={styles.successTitle}>Free Plan Selected</h2>
          <p style={{ color: "#666", marginBottom: "2rem" }}>No payment required for the free plan</p>
          <button onClick={() => (window.location.href = "/dashboard")} style={styles.submitButton}>
            Continue to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // Handle successful payment submission (either UPI or PayPal)
  if (paymentSubmitted) { // This now triggers for both UPI and successful PayPal backend update
    return (
      <div style={styles.successContainer}>
        <div style={styles.successCard}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.successTitle}>Payment Processed</h2>
          <p style={{ color: "#666", marginBottom: "2rem" }}>
            Your transaction has been processed successfully.
          </p>

          <div style={styles.transactionBox}>
            <p style={{ fontSize: "0.875rem", color: "#666", marginBottom: "0.5rem" }}>Transaction ID</p>
            <p style={{ fontFamily: "monospace", fontSize: "0.875rem", fontWeight: "500" }}>{transactionId}</p>
          </div>

          <p style={{ fontSize: "0.875rem", color: "#666", marginBottom: "2rem" }}>
            Your plan has been updated. You will receive an email confirmation shortly.
          </p>

          <button onClick={() => (window.location.href = "/dashboard")} style={styles.submitButton}>
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (loadingSettings) {
    return (
      <div style={styles.loadingContainer}>
        <span>Loading payment settings...</span>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.maxWidth}>
        {settingsError && (
          <div style={styles.errorBanner}>
            ⚠️ Unable to load latest payment settings. Using default settings.
            <br />
            <button style={styles.refreshButton} onClick={() => window.location.reload()}>
              Refresh Page
            </button>
          </div>
        )}

        {!paymentSettings.isActive && (
          <div style={styles.errorBanner}>⚠️ Payment system is currently unavailable. Please try again later.</div>
        )}

        <div style={styles.header}>
          <h1 style={styles.title}>Complete Your Payment</h1>
          <p style={styles.subtitle}>Choose your preferred payment method</p>
        </div>

        <div style={styles.grid}>
          {/* Payment Details */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Order Summary</h3>
              <span style={styles.badge}>{planName} Plan</span>
            </div>

            <div style={styles.flexBetween}>
              <span style={{ color: "#666" }}>Plan</span>
              <span style={{ fontWeight: "500" }}>{planName}</span>
            </div>
            <div style={styles.flexBetween}>
              <span style={{ color: "#666" }}>Billing Cycle</span>
              <span style={{ fontWeight: "500", textTransform: "capitalize" }}>{billingCycle}</span>
            </div>
            <hr style={styles.separator} />
            <div style={styles.totalAmount}>
              <span style={{ fontSize: "1.125rem", fontWeight: "600" }}>Total Amount</span>
              <span style={styles.amount}>${amount.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Select Payment Method</h3>
            <div
              style={styles.paymentOptionCard}
              onClick={() => { setShowModal('upi'); setTransactionId(''); setPaypalPaid(false); }}
            >
              <h4 style={styles.paymentOptionTitle}>🇮🇳 Pay with UPI (India Only)</h4>
              <p style={styles.paymentOptionDescription}>Scan QR or use UPI ID</p>
            </div>
            <div
              style={styles.paymentOptionCard}
              onClick={() => { setShowModal('paypal'); setTransactionId(''); setPaypalPaid(false); }}
            >
              <h4 style={styles.paymentOptionTitle}>💳 Pay with PayPal (International)</h4>
              <p style={styles.paymentOptionDescription}>Securely pay with your PayPal account</p>
            </div>
          </div>
        </div>

        {/* Modals are rendered here based on showModal state */}
        {showModal === "upi" && (
          <UpiModal
            setShowModal={setShowModal}
            paymentSettings={paymentSettings}
            amount={amount}
            transactionId={transactionId}
            setTransactionId={setTransactionId}
            isSubmitting={isSubmitting}
            paypalPaid={paypalPaid}
            showToast={showToast}
            handleSubmitTransaction={handleSubmitTransaction}
            handleCopyUPI={handleCopyUPI}
          />
        )}
        {showModal === "paypal" && (
          <PaypalModal
            setShowModal={setShowModal}
            paypalConfigured={paypalConfigured}
            paypalPaid={paypalPaid}
            initialOptions={initialOptions}
            createOrder={createOrder}
            onApprove={onApprove}
            onError={onError}
            onCancel={onCancel}
            transactionId={transactionId}
            setTransactionId={setTransactionId}
            isSubmitting={isSubmitting}
            paymentSettings={paymentSettings}
            handleSubmitTransaction={handleSubmitTransaction}
          />
        )}

        <div style={styles.footer}>
          <p>Your payment will be verified within 24 hours.</p>
          <p>You will receive an email confirmation once verified.</p>
        </div>
      </div>
    </div>
  )
}

export default PaymentPage